import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { type StateSpec, stateToCss } from "../helpers";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

const UncheckedIcon = <span className="RmhCheckbox-box" />;

const CheckedIcon = (
  <span className="RmhCheckbox-box">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="15"
      height="12"
      viewBox="0 0 15 12"
      fill="none"
    >
      <path
        d="M4.65833 8.81667L1.18333 5.34167L0 6.51667L4.65833 11.175L14.6583 1.175L13.4833 0L4.65833 8.81667Z"
        fill="white"
      />
    </svg>
  </span>
);

const IndeterminateIcon = (
  <span className="RmhCheckbox-box">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="2"
      viewBox="0 0 12 2"
      fill="none"
    >
      <path d="M11.6667 1.66667H0V0H11.6667V1.66667Z" fill="white" />
    </svg>
  </span>
);

type CheckboxColorSpec = {
  unchecked: {
    enabled: StateSpec;
    hovered: StateSpec;
    focused: StateSpec;
    pressed: StateSpec;
    disabled: StateSpec;
  };
  filled: {
    enabled: StateSpec;
    focused: StateSpec;
    disabled: StateSpec;
  };
};

// Compound selectors targeting `.RmhCheckbox-box`. The unchecked states are
// gated with `:not(.Mui-checked, .MuiCheckbox-indeterminate)` so they only
// apply when the box is in its outline form.
const UNCHECKED_NOT = ":not(.Mui-checked, .MuiCheckbox-indeterminate)";
const BOX = ".RmhCheckbox-box";

const CHECKBOX_SELECTORS = {
  uncheckedEnabled: `& ${BOX}`,
  uncheckedHovered: `&:hover:not(.Mui-disabled)${UNCHECKED_NOT} ${BOX}`,
  uncheckedFocused: `&.Mui-focusVisible${UNCHECKED_NOT} ${BOX}`,
  uncheckedPressed: `&:active:not(.Mui-disabled)${UNCHECKED_NOT} ${BOX}`,
  uncheckedDisabled: `&.Mui-disabled${UNCHECKED_NOT} ${BOX}`,
  filledEnabled: `&.Mui-checked ${BOX}, &.MuiCheckbox-indeterminate ${BOX}`,
  filledFocused: `&.Mui-focusVisible.Mui-checked ${BOX}, &.Mui-focusVisible.MuiCheckbox-indeterminate ${BOX}`,
  filledDisabled: `&.Mui-disabled.Mui-checked ${BOX}, &.Mui-disabled.MuiCheckbox-indeterminate ${BOX}`,
};

const colorStyle =
  (build: (rmh: Rmh) => CheckboxColorSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      [CHECKBOX_SELECTORS.uncheckedEnabled]: stateToCss(spec.unchecked.enabled),
      [CHECKBOX_SELECTORS.uncheckedHovered]: stateToCss(spec.unchecked.hovered),
      [CHECKBOX_SELECTORS.uncheckedFocused]: stateToCss(spec.unchecked.focused),
      [CHECKBOX_SELECTORS.uncheckedPressed]: stateToCss(spec.unchecked.pressed),
      [CHECKBOX_SELECTORS.uncheckedDisabled]: stateToCss(
        spec.unchecked.disabled
      ),
      // Filled box overrides borderColor to transparent so the underlying
      // outline never bleeds through the filled background.
      [CHECKBOX_SELECTORS.filledEnabled]: {
        ...stateToCss(spec.filled.enabled),
        borderColor: "transparent",
      },
      [CHECKBOX_SELECTORS.filledFocused]: stateToCss(spec.filled.focused),
      [CHECKBOX_SELECTORS.filledDisabled]: stateToCss(spec.filled.disabled),
    };
  };

const primarySpec = (rmh: Rmh): CheckboxColorSpec => ({
  unchecked: {
    enabled: { borderColor: rmh.border["4-faint"].rest },
    hovered: { borderColor: rmh.border["4-faint"].hover },
    focused: {
      borderColor: rmh.border["1-surface"].hover,
      ring: rmh.focus.primary,
    },
    pressed: { borderColor: rmh.border["1-surface"].active },
    disabled: { borderColor: rmh.stateBorder.disabled },
  },
  filled: {
    enabled: { backgroundColor: rmh.paper["5-brandFilled"].rest },
    focused: {
      backgroundColor: rmh.paper["5-brandFilled"].rest,
      ring: rmh.focus.primary,
    },
    disabled: { backgroundColor: rmh.stateBackground.disabled },
  },
});

const errorSpec = (rmh: Rmh): CheckboxColorSpec => ({
  unchecked: {
    enabled: { borderColor: rmh.border["8-error"].rest },
    hovered: { borderColor: rmh.border["8-error"].rest },
    focused: {
      borderColor: rmh.border["8-error"].rest,
      ring: rmh.focus.error,
    },
    pressed: { borderColor: rmh.border["8-error"].rest },
    disabled: { borderColor: rmh.stateBorder.disabled },
  },
  filled: {
    enabled: { backgroundColor: rmh.paper["8-errorFilled"].rest },
    focused: {
      backgroundColor: rmh.paper["8-errorFilled"].rest,
      ring: rmh.focus.error,
    },
    // Spec is sparse for Error filled-disabled; we mirror Primary so the
    // visual stays consistent. Flag for design confirmation.
    disabled: { backgroundColor: rmh.stateBackground.disabled },
  },
});

export const MuiCheckbox: NonNullable<Components<Theme>["MuiCheckbox"]> = {
  defaultProps: {
    disableRipple: true,
    color: "primary",
    size: "medium",
    icon: UncheckedIcon,
    checkedIcon: CheckedIcon,
    indeterminateIcon: IndeterminateIcon,
  },
  styleOverrides: {
    root: ({ theme }) => ({
      padding: 0,
      // Glyph color cascades to inner SVG via currentColor.
      color: theme.vars!.palette.rmh.text.staticWhite,
      "&.Mui-disabled": { color: theme.vars!.palette.rmh.text.disabled },
      "& .RmhCheckbox-box": {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        aspectRatio: "1 / 1",
        borderRadius: RadiusToken.XS,
        boxSizing: "border-box",
        borderStyle: "solid",
        borderWidth: theme.spacing(0.25),
        borderColor: "transparent",
        backgroundColor: theme.vars!.palette.rmh.paper["1-surface"].rest,
        transition: theme.transitions.create(
          ["background-color", "border-color", "box-shadow"],
          { duration: theme.transitions.duration.shortest }
        ),
      },
      "& .RmhCheckbox-box > svg": { width: "80%", height: "80%" },
    }),
  },
  variants: [
    { props: { size: "small" }, style: { width: "1rem", height: "1rem" } },
    {
      props: { size: "medium" },
      style: { width: "1.25rem", height: "1.25rem" },
    },
    {
      props: { size: "large" },
      style: { width: "1.75rem", height: "1.75rem" },
    },
    { props: { color: "primary" }, style: colorStyle(primarySpec) },
    { props: { color: "error" }, style: colorStyle(errorSpec) },
  ],
};
